import { SecuredRestService } from "shared";
import type {
    ILegalBisGroup,
    ILegalBisUnit,
    TLegalCountryCode,
    ILegalOrgsListParams,
    ILegalOrgListItem,
    ILegalOrg,
    ILegalOrgsService,
} from "../types/legal_orgs_service_types";

/**
 * Внутренний cdoc отдаёт JSON-массив с Content-Type application/json.
 * cdoc-ext (`LegalOrgController`) проксирует `RestTemplate` как Java String → часто text/plain и тело
 * приходит в `fetch` как строка `"[{...}]"`, а не как массив — старый `Array.isArray` давал [].
 */
const EXPIRED_LEGAL_ORGS_BODY = "EXPIRED";

function coerceLegalOrgsJsonArray<T>(res: unknown): T[] {
    if (Array.isArray(res)) {
        return res as T[];
    }
    if (typeof res === "string") {
        const s = res.trim();
        if (s === "" || s === EXPIRED_LEGAL_ORGS_BODY) {
            return [];
        }
        try {
            const parsed: unknown = JSON.parse(s);
            if (Array.isArray(parsed)) {
                return parsed as T[];
            }
        } catch {
            return [];
        }
        return [];
    }
    if (res != null && typeof res === "object") {
        const o = res as Record<string, unknown>;
        for (const key of ["orgs", "data", "items", "result"] as const) {
            const v = o[key];
            if (Array.isArray(v)) {
                return v as T[];
            }
        }
    }
    return [];
}

function coerceLegalOrgsJsonObject<T>(res: unknown): T {
    if (typeof res === "string") {
        const s = res.trim();
        if (s === "" || s === EXPIRED_LEGAL_ORGS_BODY) {
            throw new Error("legal-orgs: empty or EXPIRED response");
        }
        return JSON.parse(s) as T;
    }
    return res as T;
}

export class LegalOrgsService extends SecuredRestService implements ILegalOrgsService {
    private appendExtUserId(request: Record<string, string>, extUserId: string | null): Record<string, string> {
        if (extUserId) {
            request.extUserId = extUserId;
        }
        return request;
    }

    getBisGroups = (extUserId: string | null = null): Promise<ILegalBisGroup[]> => {
        return this.get<ILegalBisGroup[]>({
            url: "legal-orgs/bisgroups",
            request: this.appendExtUserId({}, extUserId),
        }).then((res) => coerceLegalOrgsJsonArray<ILegalBisGroup>(res));
    };

    getBisUnits = (extUserId: string | null = null): Promise<ILegalBisUnit[]> => {
        return this.get<ILegalBisUnit[]>({
            url: "legal-orgs/bisunits",
            request: this.appendExtUserId({}, extUserId),
        }).then((res) => coerceLegalOrgsJsonArray<ILegalBisUnit>(res));
    };

    getCountries = (extUserId: string | null = null): Promise<TLegalCountryCode[]> => {
        return this.get<TLegalCountryCode[]>({
            url: "legal-orgs/countries",
            request: this.appendExtUserId({}, extUserId),
        }).then((res) => coerceLegalOrgsJsonArray<TLegalCountryCode>(res));
    };

    getOrgs = (
        params?: Partial<ILegalOrgsListParams>,
        extUserId: string | null = null
    ): Promise<ILegalOrgListItem[]> => {
        const request: Record<string, string> = {};
        if (params?.bg_id != null) request.bg_id = String(params.bg_id);
        if (params?.bu_id != null) request.bu_id = String(params.bu_id);
        if (params?.country_code != null) request.country_code = params.country_code;
        this.appendExtUserId(request, extUserId);

        return this.get<ILegalOrgListItem[]>({
            url: "legal-orgs/orgs",
            request: request,
        }).then((res) => coerceLegalOrgsJsonArray<ILegalOrgListItem>(res));
    };

    getOrg = (ya_id: string, extUserId: string | null = null): Promise<ILegalOrg> => {
        return this.get<ILegalOrg>({
            url: "legal-orgs/org",
            request: this.appendExtUserId({ ya_id }, extUserId),
        }).then((res) => coerceLegalOrgsJsonObject<ILegalOrg>(res));
    };
}
